import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle, PencilSimple, XCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import useAdminLoad from "@/hooks/useAdminLoad";
import { LoadState, Panel, Pill } from "@/components/admin/AdminUI";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";

export const REVIEWER_KEYS = ["reviewer_access_enabled", "reviewer_phones"];
const E164 = /^\+[1-9]\d{7,14}$/;
const MAX_PHONES = 10;
// +15550100199 → +1 555-010-0199 (US/Canada); other countries stay as stored.
export const formatPhone = (p) => (/^\+1\d{10}$/.test(p) ? `+1 ${p.slice(2, 5)}-${p.slice(5, 8)}-${p.slice(8)}` : p);
const normalize = (raw) => raw.split(/[\n,]+/).map((x) => x.replace(/[\s\-().]/g, "")).filter(Boolean);
const putSetting = (key, value) => api(`/admin/settings/${key}`, { method: "PUT", body: { value } });

function Phones({ status, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const list = normalize(text);
  const bad = list.find((p) => !E164.test(p));
  const error = bad ? `${bad} isn't an E.164 number (like +15551234567).` : list.length > MAX_PHONES ? `At most ${MAX_PHONES} numbers.` : null;
  const accounts = Object.fromEntries((status.accounts || []).map((a) => [a.phone, a]));

  async function save() {
    setBusy(true);
    try { await putSetting("reviewer_phones", [...new Set(list)]); toast.success("Reviewer numbers saved."); setEdit(false); onSaved(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }

  if (edit) return (
    <div className="space-y-2" data-testid="reviewer-phones-editor">
      <Label htmlFor="reviewer-phones-input">Reviewer numbers (one per line, E.164)</Label>
      <Textarea id="reviewer-phones-input" rows={3} value={text} onChange={(e) => setText(e.target.value)} aria-invalid={!!error} className="bg-white font-mono text-sm" data-testid="reviewer-phones-input" />
      {error && <p className="cw-error text-xs" role="alert" data-testid="reviewer-phones-error">{error}</p>}
      <div className="flex gap-2">
        <Button size="sm" onClick={save} disabled={busy || !!error} data-testid="reviewer-phones-save-button">{busy ? "Saving…" : "Save numbers"}</Button>
        <Button size="sm" variant="ghost" onClick={() => setEdit(false)} data-testid="reviewer-phones-cancel-button">Cancel</Button>
      </div>
    </div>
  );
  return (
    <div className="space-y-2" data-testid="reviewer-phones">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-medium">Reviewer numbers</p>
        <Button size="sm" variant="outline" onClick={() => { setText((status.phones || []).join("\n")); setEdit(true); }} data-testid="reviewer-phones-edit-button"><PencilSimple size={14} />Edit</Button>
      </div>
      <ul className="divide-y divide-border rounded-xl border border-border">
        {(status.phones || []).map((p) => {
          const a = accounts[p];
          return (
            <li key={p} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm" data-testid={`reviewer-phone-${p.replace("+", "")}`}>
              <span className="font-mono tabular-nums">{formatPhone(p)}</span>
              <span className="flex items-center gap-2 text-xs">
                {a?.exists ? <Pill tone="success"><CheckCircle size={12} weight="fill" />Account exists</Pill> : <Pill tone="warning"><XCircle size={12} />No account yet</Pill>}
                <span className="cw-muted">{a?.last_login_at ? `Last sign-in ${formatDate(a.last_login_at)}` : "Never signed in"}</span>
              </span>
            </li>
          );
        })}
        {!status.phones?.length && <li className="cw-muted px-3 py-2 text-sm">No numbers.</li>}
      </ul>
    </div>
  );
}

export default function ReviewerAccessCard({ onChange }) {
  const { data, error, loading, reload, setData } = useAdminLoad("/admin/reviewer");
  const [ask, setAsk] = useState(null); // "on" | "off" | "seed"
  const refresh = () => { reload(); onChange?.(); };

  const confirm = async () => {
    if (ask === "seed") {
      const res = await api("/admin/reviewer/seed", { method: "POST" });
      if (res?.status) setData(res.status); else reload();
      toast.success("Demo profiles recreated.");
      return;
    }
    await putSetting("reviewer_access_enabled", ask === "on");
    toast.success(ask === "on" ? "Reviewer sign-in is on." : "Reviewer sign-in is off.");
    refresh();
  };

  return (
    <Panel className="px-6 py-5" data-testid="reviewer-access-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <h2 className="font-semibold">Reviewer access</h2>
          <p className="cw-soft mt-1 text-sm" data-testid="reviewer-help">Turn this on before you submit a build for review and off after approval. Every update needs review again.</p>
        </div>
        {data && (
          <div className="flex h-11 items-center gap-3">
            <Switch checked={!!data.enabled} onCheckedChange={(v) => setAsk(v ? "on" : "off")} aria-label="Reviewer access" data-testid="reviewer-access-switch" />
            <span className="cw-soft text-sm" data-testid="reviewer-access-state">{data.enabled ? "On" : "Off"}</span>
          </div>
        )}
      </div>
      <LoadState loading={loading && !data} error={error} onRetry={reload}>
        {data && (
          <div className="mt-4 space-y-4">
            <Phones status={data} onSaved={refresh} />
            <p className="cw-soft text-sm" data-testid="reviewer-code-note">
              The sign-in code is stored in Secrets (REVIEWER_SMS_CODE). It isn&apos;t shown here.{" "}
              {data.code_configured ? <Pill tone="success">Set</Pill> : <Pill tone="warning">Not set yet</Pill>}
            </p>
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-[var(--cw-surface-tertiary)] px-4 py-3 text-sm" data-testid="reviewer-status">
              <span><span className="tabular-nums" data-testid="reviewer-demo-count">{data.demo_profiles ?? 0}{data.demo_target != null ? ` of ${data.demo_target}` : ""}</span> demo profiles
                {data.logins != null && <span className="cw-muted"> · <span className="tabular-nums" data-testid="reviewer-login-count">{data.logins}</span> reviewer sign-in{data.logins === 1 ? "" : "s"}</span>}</span>
              <Button size="sm" variant="outline" onClick={() => setAsk("seed")} data-testid="reviewer-seed-button">Recreate demo profiles</Button>
            </div>
          </div>
        )}
      </LoadState>
      <ConfirmDialog open={!!ask} onOpenChange={(o) => !o && setAsk(null)} onConfirm={confirm} testId="reviewer-confirm-dialog" destructive={false}
        title={ask === "on" ? "Turn on reviewer sign-in for Apple and Google review?" : ask === "off" ? "Turn off reviewer sign-in?" : "Recreate the demo profiles?"}
        description={ask === "on" ? "The reviewer numbers can sign in with the code from Secrets. No SMS is sent." : ask === "off" ? "Reviewer numbers can no longer sign in." : "Creates any missing reviewer accounts and marks the demo profiles again. Safe to repeat."}
        confirmLabel={ask === "on" ? "Turn on" : ask === "off" ? "Turn off" : "Recreate"} />
    </Panel>
  );
}
